import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { ArrowLeft, FileText, Download, Share2, Trash2, Eye, Loader2, Link2, Copy, Clock, EyeOff, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';

export default function ReportsPage({ clientId, navigate }) {
  const { user } = useAuth();
  const isInternal = user?.role === 'super_admin' || user?.role === 'internal';
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shareDialog, setShareDialog] = useState(false);
  const [shareReportId, setShareReportId] = useState(null);
  const [shareLinks, setShareLinks] = useState([]);
  const [creatingLink, setCreatingLink] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const fetchReports = async () => {
    try {
      const query = clientId ? `?client_id=${clientId}` : '';
      const data = await api(`/api/reports${query}`);
      setReports(data);
    } catch (err) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReports(); }, [clientId]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this report?')) return;
    try {
      await api(`/api/reports/${id}`, { method: 'DELETE' });
      toast.success('Report deleted');
      fetchReports();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDownloadPdf = async (reportId) => {
    setDownloadingId(reportId);
    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${backendUrl}/api/reports/${reportId}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('PDF generation failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'report.pdf';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded!');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleToggleClientVisible = async (reportId, currentVisible) => {
    try {
      await api(`/api/reports/${reportId}/toggle-client-visible`, {
        method: 'PUT',
        body: JSON.stringify({ show_to_client: !currentVisible })
      });
      toast.success(!currentVisible ? 'Report visible to client' : 'Report hidden from client');
      fetchReports();
    } catch (err) {
      toast.error(err.message);
    }
  };


  const openShareDialog = async (reportId) => {
    setShareReportId(reportId);
    setShareDialog(true);
    try {
      const links = await api(`/api/share-links?report_id=${reportId}`);
      setShareLinks(links);
    } catch (err) {
      setShareLinks([]);
    }
  };

  const createShareLink = async () => {
    setCreatingLink(true);
    try {
      const link = await api('/api/share-links', {
        method: 'POST',
        body: JSON.stringify({ report_id: shareReportId, expires_days: 7 })
      });
      toast.success('Share link created!');
      setShareLinks(prev => [link, ...prev]);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCreatingLink(false);
    }
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/shared/${token}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard!');
  };

  return (
    <div className="animate-fade-in-up">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('clients')}>
            <ArrowLeft size={18} />
          </Button>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Report Snapshots
            </h1>
            <p className="text-sm text-muted-foreground">Saved report snapshots with locked data</p>
          </div>
        </div>
        {clientId && (
          <Button variant="outline" onClick={() => navigate('dashboard', { clientId })}>
            Back to Dashboard
          </Button>
        )}
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1,2,3].map(i => <Skeleton key={i} className="h-24 w-full rounded-xl" />)}
        </div>
      ) : reports.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <FileText size={40} className="mx-auto text-muted-foreground/40 mb-4" />
            <h3 className="text-lg font-medium mb-2">No reports yet</h3>
            <p className="text-sm text-muted-foreground">Generate a report from the dashboard</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4" data-testid="snapshots-table">
          {reports.map((report) => (
            <Card key={report._id} className="metric-card-hover">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white" style={{ background: '#1f2a4d' }}>
                      <FileText size={18} />
                    </div>
                    <div>
                      <h3 className="font-semibold">{report.client_name}</h3>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                        <span>{report.date_range?.start} to {report.date_range?.end}</span>
                        <span>&middot;</span>
                        <span>Created {new Date(report.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge 
                      variant={report.status === 'finalized' ? 'default' : 'secondary'} 
                      className={report.status === 'finalized' ? 'bg-emerald-600' : ''}
                    >
                      {report.status === 'finalized' ? 'Finalized' : 'Draft'}
                    </Badge>
                    {isInternal && (
                      <Button
                        variant={report.show_to_client ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => handleToggleClientVisible(report._id, report.show_to_client)}
                        className={report.show_to_client ? 'bg-blue-600 hover:bg-blue-700' : ''}
                        title={report.show_to_client ? 'Visible to client — click to hide' : 'Hidden from client — click to show'}
                      >
                        {report.show_to_client ? <><Users size={14} className="mr-1" /> Client: Yes</> : <><EyeOff size={14} className="mr-1" /> Client: No</>}
                      </Button>
                    )}
                    {!isInternal && report.show_to_client && (
                      <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-[10px]">Shared with you</Badge>
                    )}
                    <Button
                      variant="outline" size="sm"
                      onClick={() => navigate('report-builder', { reportId: report._id, clientId: report.client_id })}
                      data-testid={`view-report-${report._id}`}
                    >
                      <Eye size={14} className="mr-1" /> View
                    </Button>
                    <Button
                      variant="outline" size="sm"
                      onClick={() => handleDownloadPdf(report._id)}
                      disabled={downloadingId === report._id}
                      data-testid={`download-report-${report._id}`}
                    >
                      {downloadingId === report._id ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} className="mr-1" />}
                      PDF
                    </Button>
                    <Button
                      variant="outline" size="sm"
                      onClick={() => openShareDialog(report._id)}
                      data-testid={`share-report-${report._id}`}
                    >
                      <Share2 size={14} className="mr-1" /> Share
                    </Button>
                    <Button
                      variant="ghost" size="icon" className="h-8 w-8 text-destructive"
                      onClick={() => handleDelete(report._id)}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Share Dialog */}
      <Dialog open={shareDialog} onOpenChange={setShareDialog}>
        <DialogContent className="max-w-md" data-testid="share-link-dialog">
          <DialogHeader>
            <DialogTitle>Share Report</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Button onClick={createShareLink} disabled={creatingLink} className="w-full" data-testid="share-report-button" style={{ background: '#1f2a4d' }}>
              {creatingLink ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Link2 size={14} className="mr-2" />}
              Generate New Share Link (7 days)
            </Button>

            {shareLinks.length > 0 && (
              <div className="space-y-3">
                <Separator />
                <h4 className="text-sm font-medium">Active Links</h4>
                {shareLinks.map((link, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input
                      readOnly
                      value={`${window.location.origin}/shared/${link.token}`}
                      className="text-xs flex-1"
                      data-testid="share-link-input"
                    />
                    <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => copyLink(link.token)} data-testid="share-link-copy-button">
                      <Copy size={14} />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
